import Guid from "common/values/guid/guid";
import Individual, { IndividualJSON } from "marketplace/entities/individual/individual";
import EntityVendorRepresentative from "work/entities/entity-vendor-representative/entity-vendor-representative";
import EntityMember from "work/values/entity-member/entity-member";
import VendorTeam from "work/values/vendor-team/vendor-team";

export interface DetailedVendorTeamJSON {
  leader?: IndividualJSON;
  members: IndividualJSON[];
  marketplaceId?: string;
  description?: string;
}

export default class DetailedVendorTeam {
  leader?: Individual;
  members: Individual[] = [];
  marketplaceId?: Guid;
  description?: string;

  constructor(
    leader: Individual | undefined,
    members: Individual[],
    marketplaceId?: Guid,
    description?: string
  ) {
    this.leader = leader;
    this.members = members;
    this.marketplaceId = marketplaceId;
    this.description = description;
  }

  public toVendorTeam(): VendorTeam {
    if (!this.leader) throw new Error("Leader is required");
    if (!this.leader.userId) throw new Error("Leader userId is required");
    if (!this.leader.entityId) throw new Error("Leader entityId is required");

    return new VendorTeam(
      new EntityVendorRepresentative(
        this.leader.userId,
        this.leader.entityId
      ),
      this.members.filter(member => member.userId && member.entityId).map((member) => new EntityMember(member.userId!, member.entityId!)),
      this.marketplaceId
    );
  }

  public isEqualTo(other?: unknown): boolean {
    if (!other || !(other instanceof DetailedVendorTeam)) return false;
    if ((!this.leader && other.leader) || (this.leader && !this.leader?.isEqualTo(other.leader))) return false;
    if (this.members.length !== other.members.length) return false;
    for (const member of this.members) {
      if (!other.members.some((m) => m.isEqualTo(member))) return false;
    }
    return true;
  }

  public clone(): DetailedVendorTeam {
    return new DetailedVendorTeam(
      this.leader?.clone(),
      this.members.map((member) => member.clone()),
      this.marketplaceId,
      this.description
    );
  }

  public toJSON(): DetailedVendorTeamJSON {
    return {
      leader: this.leader?.toJSON(),
      members: this.members.map((member) => member.toJSON()),
      marketplaceId: this.marketplaceId?.value,
      description: this.description,
    };
  }

  public static fromJSON(json: DetailedVendorTeamJSON): DetailedVendorTeam {
    const leader = json.leader ? Individual.fromJSON(json.leader) : undefined;
    if (!leader) throw new Error("Leader is required");
    return new DetailedVendorTeam(
      leader,
      json.members.map((member) => Individual.fromJSON(member)),
      json.marketplaceId ? new Guid(json.marketplaceId) : undefined,
      json.description
    );
  }
}
